import {
  CloseCircleFilled,
  DeleteOutlined,
  EditOutlined,
  LeftOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  RightOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Card, Col, DatePicker, Form, Input, message, Modal, Popconfirm, Row, Segmented, Select, Tooltip } from "antd";
import type { TableColumnsType } from "antd";
import { expenseApi } from "api/expense.api";
import { AppButton, AppTable, AppTableToolbar } from "components/antd-ui";
import { ImportExcelModal, ImportExcelResult } from "components/Import/ImportExcelModal";
import { InputNumber } from "components/Input/InputNumber";
import { StatusCell, TableContent, TableLayout, TableLayoutFooter, TableTotals } from "components/table";
import ReactECharts from "echarts-for-react";
import { useClientPaging } from "hooks/useClientPaging";
import { useSearchInput } from "hooks/useSearchInput";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { userStore } from "store/userStore";
import {
  Expense,
  ExpenseCategoryPoint,
  expenseCategoryLabel,
  expenseCategoryOptions,
  ExpenseMonthlyPoint,
  expenseStatusOptions,
  expenseStatusTrans,
  ExpenseSummary,
  expenseTypeLabel,
  expenseTypeOptions,
} from "types/expense";
import { RevenuePreset, revenuePresetOptions } from "types/revenue";
import { formatVND, getTitle } from "utils";
import { can } from "utils/auth";
import { barValueLabel } from "utils/chart";
import dayjs, { Dayjs } from "utils/dayjs";

const expensePath = "/expense";
const requiredRules = [{ required: true, message: "Bắt buộc" }];

const formatMoney = (num: number) => `${formatVND(Math.round(num || 0))}đ`;

// Nhãn trục số rút gọn (20tr, 1,2 tỷ) để không chồng chữ khi biểu đồ chỉ rộng nửa trang
const formatAxisMoney = (value: number) => {
  if (Math.abs(value) >= 1_000_000_000) return `${+(value / 1_000_000_000).toFixed(2)} tỷ`.replace(".", ",");
  if (Math.abs(value) >= 1_000_000) return `${+(value / 1_000_000).toFixed(1)}tr`.replace(".", ",");
  return formatVND(value);
};

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, index) => CURRENT_YEAR - index);

interface ExpenseForm {
  name: string;
  category: Expense["category"];
  amount: number | string;
  type: Expense["type"];
  expenseDate: Dayjs;
  status: Expense["status"];
}

export const ExpensePage = observer(({ title = "" }) => {
  const [form] = Form.useForm<ExpenseForm>();

  const [preset, setPreset] = useState<RevenuePreset>("THIS_MONTH");
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [monthValue, setMonthValue] = useState<Dayjs>(dayjs().startOf("month"));
  const searchInput = useSearchInput();
  const search = searchInput.debounced;
  const [statusFilter, setStatusFilter] = useState<Expense["status"] | undefined>();
  const [categoryFilter, setCategoryFilter] = useState<Expense["category"] | undefined>();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [monthlyRows, setMonthlyRows] = useState<ExpenseMonthlyPoint[]>([]);
  const [categoryRows, setCategoryRows] = useState<ExpenseCategoryPoint[]>([]);
  const [chartLoading, setChartLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Expense | null>(null);
  const [saving, setSaving] = useState(false);

  const [importOpen, setImportOpen] = useState(false);

  const isAdmin = !!userStore.info.role?.isAdmin;
  const paging = useClientPaging(expenses);

  const canCreate = can(expensePath, "CREATE");
  const canUpdate = can(expensePath, "UPDATE");
  const canDelete = can(expensePath, "DELETE");

  useEffect(() => {
    document.title = getTitle(title);
  }, [title]);

  const fetchSummary = useCallback(async (p: RevenuePreset) => {
    setSummaryLoading(true);
    try {
      const res = await expenseApi.summary(p);
      setSummary(res.data);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchCharts = useCallback(async (y: number) => {
    setChartLoading(true);
    try {
      const [monthlyRes, categoryRes] = await Promise.all([expenseApi.monthlyChart(y), expenseApi.categoryChart(y)]);
      setMonthlyRows(monthlyRes.data.rows || []);
      setCategoryRows(categoryRes.data.rows || []);
    } finally {
      setChartLoading(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await expenseApi.findAll({
        month: monthValue.month() + 1,
        year: monthValue.year(),
        search,
        status: statusFilter,
        category: categoryFilter,
      });
      setExpenses(res.data.expenses || []);
      setTotalAmount(res.data.totalAmount || 0);
      setPaidAmount(res.data.paidAmount || 0);
    } finally {
      setLoading(false);
    }
  }, [monthValue, search, statusFilter, categoryFilter]);

  useEffect(() => {
    fetchSummary(preset);
  }, [fetchSummary, preset]);

  useEffect(() => {
    fetchCharts(year);
  }, [fetchCharts, year]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const refreshAll = useCallback(() => {
    fetchList();
    fetchSummary(preset);
    fetchCharts(year);
  }, [fetchList, fetchSummary, fetchCharts, preset, year]);

  const importExpenseFile = useCallback(async (file: File) => {
    const res = await expenseApi.importExcel(file);
    return (res.data.results || []) as ImportExcelResult[];
  }, []);

  const openCreate = () => {
    const today = dayjs();
    form.resetFields();
    form.setFieldsValue({
      category: "OTHER",
      type: "IRREGULAR",
      status: "PENDING_CONFIRM",
      expenseDate: today.isSame(monthValue, "month") ? today : monthValue.startOf("month"),
    });
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = useCallback(
    (expense: Expense) => {
      form.resetFields();
      form.setFieldsValue({
        name: expense.name,
        category: expense.category,
        amount: expense.amount,
        type: expense.type,
        expenseDate: dayjs(expense.expenseDate),
        status: expense.status,
      });
      setEditing(expense);
      setModalOpen(true);
    },
    [form],
  );

  const handleSubmit = async () => {
    const values = await form.validateFields();
    const amount = Math.round(Number(values.amount) || 0);
    if (amount <= 0) {
      message.error("Số tiền phải lớn hơn 0");
      return;
    }
    const data = {
      expense: {
        name: values.name.trim(),
        category: values.category,
        amount,
        type: values.type,
        expenseDate: values.expenseDate.format("YYYY-MM-DD"),
        status: values.status,
      },
    };

    setSaving(true);
    try {
      if (editing) {
        await expenseApi.update(editing.id, data);
      } else {
        await expenseApi.create(data);
      }
      message.success(editing ? "Cập nhật thành công!" : "Tạo mới thành công!");
      setModalOpen(false);
      refreshAll();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(
    async (expense: Expense) => {
      await expenseApi.delete(expense.id);
      message.success("Đã xóa khoản chi");
      refreshAll();
    },
    [refreshAll],
  );

  const columns = useMemo<TableColumnsType<Expense>>(
    () => [
      { title: "Mã", dataIndex: "code", key: "code", width: 110, render: (value) => <TableContent>{value || "--"}</TableContent> },
      { title: "Tên khoản chi", dataIndex: "name", key: "name", render: (value) => <TableContent>{value}</TableContent> },
      {
        title: "Danh mục",
        dataIndex: "category",
        key: "category",
        render: (value) => <TableContent>{expenseCategoryLabel(value)}</TableContent>,
      },
      {
        title: "Số tiền",
        dataIndex: "amount",
        key: "amount",
        align: "right",
        render: (value) => <span className="font-semibold">{formatMoney(value)}</span>,
      },
      { title: "Loại", dataIndex: "type", key: "type", render: (value) => <TableContent>{expenseTypeLabel(value)}</TableContent> },
      {
        title: "Thời gian",
        dataIndex: "expenseDate",
        key: "expenseDate",
        render: (value) => <TableContent>{value ? dayjs(value).format("DD/MM/YYYY") : "--"}</TableContent>,
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        render: (value) => <StatusCell value={value} trans={expenseStatusTrans} />,
      },
      ...(canUpdate || canDelete
        ? [
            {
              title: "",
              key: "actions",
              width: 90,
              render: (_: unknown, record: Expense) => (
                <div className="flex items-center gap-1">
                  {canUpdate && (
                    <AppButton tone="light" aria-label="Sửa" onClick={() => openEdit(record)} style={{ padding: "2px 6px" }}>
                      <EditOutlined />
                    </AppButton>
                  )}
                  {canDelete && (
                    <Popconfirm title="Xóa khoản chi này?" okText="Xóa" cancelText="Hủy" onConfirm={() => handleDelete(record)}>
                      <AppButton tone="light" aria-label="Xóa" style={{ padding: "2px 6px" }}>
                        <DeleteOutlined style={{ color: "#ef4444" }} />
                      </AppButton>
                    </Popconfirm>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canUpdate, canDelete, openEdit, handleDelete],
  );

  const rangeLabel = summary ? `${dayjs(summary.from).format("DD/MM/YYYY")} — ${dayjs(summary.to).format("DD/MM/YYYY")}` : "";

  const monthlyChartOption = useMemo(
    () => ({
      grid: { left: 16, right: 24, top: 32, bottom: 16, containLabel: true },
      tooltip: { trigger: "axis", valueFormatter: (value: number) => formatMoney(Number(value)) },
      xAxis: { type: "category", data: monthlyRows.map((row) => `T${row.month}`) },
      yAxis: { type: "value", axisLabel: { formatter: formatAxisMoney } },
      series: [
        {
          type: "bar",
          data: monthlyRows.map((row) => row.expense),
          itemStyle: { color: "#ef4444", borderRadius: [4, 4, 0, 0] },
          barMaxWidth: 36,
          label: barValueLabel("top"),
        },
      ],
    }),
    [monthlyRows],
  );

  const categoryChartOption = useMemo(() => {
    const sorted = [...categoryRows].reverse(); // echarts horizontal bar vẽ từ dưới lên
    return {
      grid: { left: 16, right: 72, top: 16, bottom: 16, containLabel: true },
      tooltip: { trigger: "axis", valueFormatter: (value: number) => formatMoney(Number(value)) },
      xAxis: { type: "value", axisLabel: { formatter: formatAxisMoney } },
      yAxis: {
        type: "category",
        data: sorted.map((row) => expenseCategoryLabel(row.category)),
        axisLabel: { width: 150, overflow: "truncate" },
      },
      series: [
        {
          type: "bar",
          data: sorted.map((row) => row.expense),
          itemStyle: { color: "#f58a07", borderRadius: [0, 4, 4, 0] },
          barMaxWidth: 20,
          label: barValueLabel("right"),
        },
      ],
    };
  }, [categoryRows]);

  return (
    <div className="flex flex-col gap-4">
      <Card loading={summaryLoading && !summary}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-semibold">Tổng quan chi phí</div>
          <div className="flex items-center gap-3 flex-wrap">
            {rangeLabel && <div className="text-base font-semibold text-gray-600">{rangeLabel}</div>}
            <Segmented
              value={preset}
              onChange={(value) => setPreset(value as RevenuePreset)}
              options={revenuePresetOptions.map((item) => ({ label: item.label, value: item.value }))}
            />
          </div>
        </div>

        <Row gutter={16} className="mt-4">
          <Col span={12}>
            <div className="text-xs text-gray-400 font-semibold uppercase">Tổng số lần chi</div>
            <div className="text-xl font-bold mt-1">{summary?.expenseCount ?? 0}</div>
          </Col>
          <Col span={12}>
            <div className="text-xs text-gray-400 font-semibold uppercase flex items-center gap-1">
              Tổng chi phí thực tế
              <Tooltip title="Chỉ tính các khoản chi ở trạng thái Đã thanh toán, theo thời gian phát sinh của khoản chi.">
                <QuestionCircleOutlined className="text-gray-400 cursor-help" />
              </Tooltip>
            </div>
            <div className="text-xl font-bold mt-1 text-red-600">{formatMoney(summary?.actualExpense || 0)}</div>
          </Col>
        </Row>
      </Card>

      <TableLayout
        filter={
          <AppTableToolbar
            search={{
              value: searchInput.value,
              width: 360,
              placeholder: "Tìm theo mã, tên khoản chi",
              onChange: searchInput.onChange,
              inputProps: { allowClear: { clearIcon: <CloseCircleFilled onClick={searchInput.clear} /> } },
            }}
            leftActions={
              <>
                <div className="flex items-center gap-1">
                  <AppButton tone="light" aria-label="Tháng trước" onClick={() => setMonthValue((prev) => prev.subtract(1, "month"))}>
                    <LeftOutlined />
                  </AppButton>
                  <div className="min-w-[90px] text-center font-semibold">{monthValue.format("MM/YYYY")}</div>
                  <AppButton tone="light" aria-label="Tháng sau" onClick={() => setMonthValue((prev) => prev.add(1, "month"))}>
                    <RightOutlined />
                  </AppButton>
                </div>
                {canCreate && (
                  <AppButton tone="primary" icon={<PlusOutlined />} onClick={openCreate}>
                    Thêm chi phí
                  </AppButton>
                )}
                {isAdmin && (
                  <AppButton icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>
                    Import Excel
                  </AppButton>
                )}
              </>
            }
            filterLabel={null}
            filters={[
              {
                key: "category",
                value: categoryFilter ?? "",
                options: expenseCategoryOptions,
                onChange: (value) => setCategoryFilter((value as Expense["category"]) || undefined),
                placeholder: "Danh mục",
                allowClear: true,
                width: 200,
              },
              {
                key: "status",
                value: statusFilter ?? "",
                options: expenseStatusOptions,
                onChange: (value) => setStatusFilter((value as Expense["status"]) || undefined),
                placeholder: "Trạng thái",
                allowClear: true,
                width: 170,
              },
            ]}
          />
        }
        tabs={
          <TableTotals
            items={[
              { label: `Tổng các khoản chi tháng ${monthValue.format("MM/YYYY")}`, value: formatMoney(totalAmount) },
              { label: "Đã thanh toán", value: formatMoney(paidAmount), tone: "negative" },
            ]}
          />
        }
        table={
          <AppTable<Expense> rowKey="id" loading={loading} pagination={false} dataSource={paging.pageRows} columns={columns} />
        }
        footer={
          <TableLayoutFooter
            showingFrom={paging.showingFrom}
            showingTo={paging.showingTo}
            total={paging.total}
            currentPage={paging.page}
            pageSize={paging.limit}
            onChange={paging.onChange}
          />
        }
      />

      <Card>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div className="font-semibold">Biểu đồ chi phí</div>
          <Select value={year} onChange={setYear} options={YEAR_OPTIONS.map((y) => ({ value: y, label: `Năm ${y}` }))} style={{ width: 140 }} />
        </div>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <div className="text-sm font-medium mb-2">Chi phí thực tế theo tháng</div>
            <ReactECharts option={monthlyChartOption} style={{ height: 360 }} showLoading={chartLoading} />
          </Col>
          <Col xs={24} lg={12}>
            <div className="text-sm font-medium mb-2">Chi phí thực tế theo danh mục</div>
            <ReactECharts option={categoryChartOption} style={{ height: 360 }} showLoading={chartLoading} />
          </Col>
        </Row>
      </Card>

      <ImportExcelModal
        open={importOpen}
        title="Import chi phí"
        sampleUrl="/templates/crm_expense.xlsx"
        sampleFileName="crm_expense.xlsx"
        onClose={() => setImportOpen(false)}
        onSuccess={refreshAll}
        importFile={importExpenseFile}
      />

      <Modal
        open={modalOpen}
        title={editing ? "Cập nhật chi phí" : "Thêm chi phí"}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={saving}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        width={640}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Tên khoản chi" name="name" rules={requiredRules}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Danh mục" name="category" rules={requiredRules}>
                <Select options={expenseCategoryOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Số tiền (đ)" name="amount" rules={requiredRules}>
                <InputNumber />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Loại" name="type" rules={requiredRules}>
                <Select options={expenseTypeOptions} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Thời gian" name="expenseDate" rules={requiredRules}>
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Trạng thái" name="status" rules={requiredRules}>
                <Select options={expenseStatusOptions} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
});
